import { useEffect, useState } from 'react';
import { LogOut, Moon, Sun } from 'lucide-react';
import { NavLink, Outlet } from 'react-router-dom';
import { api } from '../lib/api';
import { AlertProvider, useAlerts } from '../lib/alerts';
import { useAuth } from '../lib/auth';
import { useBranding } from '../lib/branding';
import { socket } from '../lib/socket';
import { AlertToasts } from './AlertToasts';
import { Logo } from './Logo';

function AlertsLink() {
  const { unacknowledged } = useAlerts();
  return (
    <NavLink to="/alerts">
      Alerts
      {unacknowledged > 0 && (
        <span className="nav-count" aria-label={`${unacknowledged} unacknowledged`}>
          {unacknowledged > 99 ? '99+' : unacknowledged}
        </span>
      )}
    </NavLink>
  );
}

export function AppShell() {
  const { user, logout, setTheme } = useAuth();
  const brand = useBranding();
  const dark = user?.theme === 'dark';
  const [sound, setSound] = useState(true);

  useEffect(() => {
    socket.start();
    api<{ settings: { alertSound: boolean } }>('GET', '/api/settings')
      .then((r) => setSound(r.settings.alertSound))
      .catch(() => {});
  }, []);

  return (
    <AlertProvider soundEnabled={sound}>
      <header className="app-header">
        <NavLink to="/" className="brand">
          <Logo />
          <span>{brand.productName}</span>
        </NavLink>
        <nav aria-label="Main">
          <NavLink to="/" end>
            Streams
          </NavLink>
          <AlertsLink />
          <NavLink to="/presets">Presets</NavLink>
          {user?.role === 'admin' && <NavLink to="/users">Users</NavLink>}
          {user?.role === 'admin' && <NavLink to="/settings">Settings</NavLink>}
          <NavLink to="/account">Account</NavLink>
        </nav>
        <div className="spacer" />
        <div className="user">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => void setTheme(dark ? 'light' : 'dark')}
            aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            title={dark ? 'Light mode' : 'Dark mode (control room)'}
          >
            {dark ? <Sun size={16} aria-hidden /> : <Moon size={16} aria-hidden />}
            <span className="hide-sm">{dark ? 'Light' : 'Dark'}</span>
          </button>
          <span className="email">{user?.email}</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => void logout()}>
            <LogOut size={16} aria-hidden />
            <span className="hide-sm">Sign out</span>
          </button>
        </div>
      </header>
      <AlertToasts />
      <main>
        <Outlet />
      </main>
      <footer className="app-footer">
        <span>{brand.productName}</span>
        <span>{brand.tagline}</span>
      </footer>
    </AlertProvider>
  );
}
